import { useMemo, useState } from 'react';
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { FBoxContainer, FQrCode, FRadioGroup } from '../../../../../components/baseV2';
import { DOCUMENT_SUBMISSION_METHODS } from '../../../onboarding/constants';
import QuestionnaireSectionLabel from '../QuestionnaireSectionLabel';
import { useFormReadOnly } from '../../../../common';
import { DesktopMobileCapturePollingNotice } from './components';
import {
  DOCUMENT_UPLOAD_METHOD_OPTIONS_DESKTOP,
  DOCUMENT_UPLOAD_METHOD_OPTIONS_MOBILE,
} from './constants';
import { DocumentUploadSectionContext } from './context';
import { usePollOnboardingDocuments } from './hooks';

import type { DocumentUploadSectionProps } from './types';

export default function DocumentUploadSection({
  title,
  radioLabel,
  disabled = false,
  desktopMobileCaptureMessage,
  children,
}: DocumentUploadSectionProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const readOnly = useFormReadOnly();
  const { pathname, search, hash } = useLocation();
  const [submissionMethod, setSubmissionMethod] = useState<string>(
    DOCUMENT_SUBMISSION_METHODS.UPLOADED,
  );

  const options = isMobile
    ? DOCUMENT_UPLOAD_METHOD_OPTIONS_MOBILE
    : DOCUMENT_UPLOAD_METHOD_OPTIONS_DESKTOP;

  const showDesktopMobileCaptureHandoff =
    !isMobile &&
    !readOnly &&
    submissionMethod === DOCUMENT_SUBMISSION_METHODS.CAPTURED &&
    desktopMobileCaptureMessage !== undefined;

  const isPollingDocuments = usePollOnboardingDocuments(showDesktopMobileCaptureHandoff);

  const contextValue = useMemo(
    () => ({ submissionMethod, isDesktopMobileCaptureHandoff: showDesktopMobileCaptureHandoff }),
    [submissionMethod, showDesktopMobileCaptureHandoff],
  );

  const sectionBody = (
    <>
      <QuestionnaireSectionLabel variant={isMobile ? 'h6' : 'body1'} sx={{ mb: 2 }}>
        {title}
      </QuestionnaireSectionLabel>
      <Stack gap={2}>
        {!readOnly && (
          <FRadioGroup
            disabled={disabled}
            fullWidth
            label={radioLabel}
            onChange={(event) => setSubmissionMethod(event.target.value)}
            options={options}
            row
            value={submissionMethod}
          />
        )}
        {showDesktopMobileCaptureHandoff && (
          <Stack gap={1} my={2}>
            <Stack alignItems="flex-start" direction="row" gap={2}>
              <Typography sx={{ flex: 1, textAlign: 'left' }} variant="body1">
                {desktopMobileCaptureMessage}
              </Typography>
              <FQrCode route={`${pathname}${search}${hash}`} />
            </Stack>
            <DesktopMobileCapturePollingNotice isRefreshing={isPollingDocuments} />
          </Stack>
        )}
        {children}
      </Stack>
    </>
  );

  return (
    <DocumentUploadSectionContext.Provider value={contextValue}>
      {isMobile ? sectionBody : <FBoxContainer>{sectionBody}</FBoxContainer>}
    </DocumentUploadSectionContext.Provider>
  );
}
